/**
 * THE INSTALLED GAME. Phase 23.
 *
 * Registration, the update prompt and the push subscription, all in one small file, because all
 * three are the browser's APIs and none of them is game state. The store holds what the SERVER
 * says about subscriptions; this holds what the BROWSER says.
 *
 * Registration happens in production builds only. `vite dev` serves `public/sw.js` as a file
 * with its placeholders unfilled, and a worker that precaches nothing and claims every tab is a
 * worker that makes hot reload a mystery.
 */

import type { PushKind } from '@ashport/shared'
import { PUSH_KINDS } from '@ashport/shared'

export const PUSH_LABEL: Record<PushKind, { title: string; line: string }> = {
  energy: { title: 'Your bar is full', line: 'When Energy refills, once every eight hours at most.' },
  beat: { title: 'The Beat is on your district', line: 'When the rotation lands where you stand, once in twelve hours at most.' },
  mugged: { title: 'You were mugged', line: 'When somebody takes your dirty cash. One a half hour.' },
  ping: { title: 'A test', line: 'The card sends one to this browser.' },
}

/** The kinds a player chooses. The ping is the card's own and never a switch. */
export const PUSH_CHOICES: PushKind[] = PUSH_KINDS.filter((k) => k !== 'ping')

let registration: ServiceWorkerRegistration | null = null
const updateListeners = new Set<(reg: ServiceWorkerRegistration) => void>()

export const swSupported = () => typeof navigator !== 'undefined' && 'serviceWorker' in navigator
export const pushSupported = () =>
  swSupported() && typeof window !== 'undefined' && 'PushManager' in window && 'Notification' in window

/** Register once, and turn "a new worker is waiting" into one event the shell listens for. */
export function registerServiceWorker(): void {
  if (!swSupported() || !import.meta.env.PROD) return
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((reg) => {
        registration = reg
        const waiting = () => updateListeners.forEach((fn) => fn(reg))
        if (reg.waiting && navigator.serviceWorker.controller) waiting()
        reg.addEventListener('updatefound', () => {
          const w = reg.installing
          if (!w) return
          w.addEventListener('statechange', () => {
            if (w.state === 'installed' && navigator.serviceWorker.controller) waiting()
          })
        })
      })
      .catch(() => undefined)
    // The reload on take-over, ONCE: `controllerchange` also fires on the very first install,
    // where there is nothing to reload into.
    let had = Boolean(navigator.serviceWorker.controller)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (had) window.location.reload()
      had = true
    })
  })
}

export function onUpdateReady(fn: (reg: ServiceWorkerRegistration) => void): () => void {
  updateListeners.add(fn)
  if (registration?.waiting && navigator.serviceWorker.controller) fn(registration)
  return () => updateListeners.delete(fn)
}

/** The player said yes to the new build. */
export function applyUpdate(reg: ServiceWorkerRegistration): void {
  reg.waiting?.postMessage({ type: 'SKIP_WAITING' })
}

/**
 * The registration, if there is one, NOW. Never `serviceWorker.ready`: that promise waits for an
 * active worker and waits forever under `vite dev`, where none is registered, and in any tab
 * whose worker is still installing. A read must answer.
 */
async function registered(): Promise<ServiceWorkerRegistration | null> {
  if (!swSupported()) return null
  try {
    return (await navigator.serviceWorker.getRegistration('/')) ?? null
  } catch {
    return null
  }
}

/** For a subscribe, the ACTIVE worker, but not for longer than a player would wait. */
async function ready(timeoutMs = 5000): Promise<ServiceWorkerRegistration | null> {
  if (!swSupported()) return null
  try {
    return await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs)),
    ])
  } catch {
    return null
  }
}

/** This browser's subscription, if it has one, as the server's JSON shape. */
export async function currentSubscription(): Promise<PushSubscriptionJSON | null> {
  const reg = await registered()
  if (!reg) return null
  const sub = await reg.pushManager.getSubscription().catch(() => null)
  return sub ? sub.toJSON() : null
}

function keyBytes(b64url: string): Uint8Array {
  const padded = b64url.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (b64url.length % 4)) % 4)
  const raw = atob(padded)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

/**
 * Ask the browser, then the push service. Returns the subscription JSON the server stores, or
 * null with the reason the card prints: the player said no, or this browser cannot.
 */
export async function subscribeBrowser(
  publicKey: string,
): Promise<{ sub: PushSubscriptionJSON } | { sub: null; why: string }> {
  if (!pushSupported()) return { sub: null, why: 'This browser cannot take a push. On iPhone, add the game to the home screen first.' }
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return { sub: null, why: 'Notifications are off for this site in the browser.' }
  const reg = await ready()
  if (!reg) return { sub: null, why: 'The installed worker is not running yet. Reload and try again.' }
  try {
    const sub =
      (await reg.pushManager.getSubscription()) ??
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: keyBytes(publicKey) as BufferSource,
      }))
    return { sub: sub.toJSON() }
  } catch {
    return { sub: null, why: 'The push service refused this browser. It happens on some private windows.' }
  }
}

/** Tell the browser to forget it. The server row is the caller's to remove. */
export async function unsubscribeBrowser(): Promise<string | null> {
  const reg = await registered()
  const sub = await reg?.pushManager.getSubscription().catch(() => null)
  if (!sub) return null
  const endpoint = sub.endpoint
  await sub.unsubscribe().catch(() => undefined)
  return endpoint
}
